import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import { isInterviewCompleted } from '@/utils/assignments/isInterviewCompleted';
import type { AssignmentResponse } from '@/types';

export interface ContentViewerProps {
  assignment: AssignmentResponse;
}

export function ContentViewer({ assignment }: ContentViewerProps) {
  const hasPostInterviewContent = isInterviewCompleted(assignment);

  return (
    <Tabs defaultValue='angle' className='mt-4'>
      <TabsList
        className={`grid w-full ${hasPostInterviewContent ? 'grid-cols-4' : 'grid-cols-3'}`}
      >
        <TabsTrigger value='angle' disabled={!assignment.editorialAngle}>
          Editorial Angle
          {assignment.editorialAngle && (
            <CheckCircle className='h-3 w-3 ml-1 text-green-600' />
          )}
        </TabsTrigger>
        <TabsTrigger value='briefing' disabled={!assignment.avatarBriefing}>
          Avatar Briefing
          {assignment.avatarBriefing && (
            <CheckCircle className='h-3 w-3 ml-1 text-green-600' />
          )}
        </TabsTrigger>
        <TabsTrigger value='script' disabled={!assignment.interviewScript}>
          Interview Script
          {assignment.interviewScript && (
            <CheckCircle className='h-3 w-3 ml-1 text-green-600' />
          )}
        </TabsTrigger>
        {hasPostInterviewContent && (
          <TabsTrigger value='draft' disabled={!assignment.draftContent}>
            Draft Article
            {assignment.draftContent && (
              <CheckCircle className='h-3 w-3 ml-1 text-green-600' />
            )}
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value='angle' className='mt-4'>
        {assignment.editorialAngle ? (
          <div className='p-4 bg-muted rounded-lg'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm font-medium'>Editorial Angle</span>
              <Badge variant='outline' className='text-xs'>
                {formatDate(assignment.editorialAngleGeneratedAt)}
              </Badge>
            </div>
            <p className='text-sm leading-relaxed'>
              {assignment.editorialAngle}
            </p>
          </div>
        ) : (
          <div className='p-8 text-center text-muted-foreground'>
            <p className='text-sm'>No editorial angle generated yet</p>
            <p className='text-xs text-muted-foreground mt-2'>
              Use the workflow buttons above to generate
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value='briefing' className='mt-4'>
        {assignment.avatarBriefing ? (
          <div className='p-4 bg-muted rounded-lg'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm font-medium'>Avatar Briefing</span>
              <Badge variant='outline' className='text-xs'>
                {formatDate(assignment.avatarBriefingGeneratedAt)}
              </Badge>
            </div>
            <p className='text-sm leading-relaxed'>
              {assignment.avatarBriefing}
            </p>
          </div>
        ) : (
          <div className='p-8 text-center text-muted-foreground'>
            <p className='text-sm'>No avatar briefing generated yet</p>
            <p className='text-xs text-muted-foreground mt-2'>
              {assignment.editorialAngle
                ? 'Use the workflow buttons above to generate'
                : 'Generate editorial angle first'}
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value='script' className='mt-4'>
        {assignment.interviewScript ? (
          <div className='p-4 bg-muted rounded-lg'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm font-medium'>Interview Script</span>
              <Badge variant='outline' className='text-xs'>
                {formatDate(assignment.interviewScriptGeneratedAt)}
              </Badge>
            </div>
            <p className='text-sm leading-relaxed'>
              {assignment.interviewScript}
            </p>
          </div>
        ) : (
          <div className='p-8 text-center text-muted-foreground'>
            <p className='text-sm'>No interview script generated yet</p>
            <p className='text-xs text-muted-foreground mt-2'>
              {assignment.editorialAngle
                ? 'Use the workflow buttons above to generate'
                : 'Generate editorial angle first'}
            </p>
          </div>
        )}
      </TabsContent>

      {hasPostInterviewContent && (
        <TabsContent value='draft' className='mt-4'>
          {assignment.draftContent ? (
            <div className='p-4 bg-muted rounded-lg'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium'>Draft Article</span>
                <Badge variant='outline' className='text-xs'>
                  Generated
                </Badge>
              </div>
              <p className='text-sm leading-relaxed'>
                {assignment.draftContent}
              </p>
            </div>
          ) : (
            <div className='p-8 text-center text-muted-foreground'>
              <p className='text-sm'>No draft article created yet</p>
              <p className='text-xs text-muted-foreground mt-2'>
                {assignment.transcriptUrl
                  ? 'Use the workflow buttons above to create'
                  : 'Generate transcript first'}
              </p>
            </div>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
}
